// The client half of `CameraMan` (CameraMan.cpp; docs/platform/RenderFidelity.md "Camera"): entity
// tracking with the activity border, the intro and zoom animations, `ClampToWorldBounds`, shake
// and pulse, the graphic push (`UpdateGraphicCameras`) and the player's camera control
// (`ParseInput`, `ParseZoomInput`). The sim keeps only the hashed input block and the round bounds
// (crates/sim/src/present/camera/camera_man.rs); each sim step it sends the camera view
// (crates/sim/src/view/camera.rs, "WMC1"): the step's camera calls in walk order, `CAM_TICK` where
// `CameraMan::LogicUpdate` ran with the inputs tracking and `ParseInput` read at that point, and
// the damage-pass entries. `step` replays one view; `pose` is what the renderer draws with.
// The player's input reaches the camera as in the original: the binder's values of the 22 camera
// channels (`readInput`, sampled once per step) become the Gui's input events of the camera node
// (`CameraInput`), which `ParseInput` reads. Plain TypeScript (no DOM, no three) so node tests can
// load it.
//
// Not ported: the replay snapshots (`OnReplayStart`/`OnReplayEnd`), `FudgeGuiCamera` (it moves
// `GameApp::m_GuiBackInGameCamera`, the camera of `DRL_RenderGeometry_GuiBG`, which holds only
// the front end's background: nothing in a match), the audio listener, the debug texts.

/** `GameApp::m_FrustumXSize` (0x159bd2c): the game camera's horizontal half-extent at distance 1. */
export const FRUSTUM_X = 0.1821251;
/** `TaskObject::kLogicUpdate` (0x3c888889). */
const DT = Math.fround(1 / 60);
const f = Math.fround;
/** `DAT_0159af20`: the squared-length epsilon of the camera's normalisations. */
const EPS = f(1e-9);

/** The float32 of the bit pattern `bits`. */
function f32Bits(bits: number): number {
  return new Float32Array(new Uint32Array([bits]).buffer)[0];
}

/** The channels of `CameraMan::m_Ld.m_Input` in block order (`InitLogicalGuiInput` 0xb5f7b0's
 * mapper); the binder delivers their values in this order. 10-13 are continuous channels
 * (`UserMan::Init`'s `Gui::CoreInputIsContinuous`). */
export const CAMERA_CHANNELS = [
  'CameraTrackLeft', 'CameraTrackRight', 'CameraTrackUp', 'CameraTrackDown', 'CameraAxisX', 'CameraAxisY',
  'CameraLeft', 'CameraRight', 'CameraUp', 'CameraDown', 'PointerPosNormalisedX', 'PointerPosNormalisedY',
  'PointerDeltaX', 'PointerDeltaY', 'CameraZoomToggle', 'CameraZoomIn', 'CameraZoomOut', 'CameraZoomInPad',
  'CameraZoomOutPad', 'SelectRight', 'CameraCentre', 'AnyInput',
] as const;
const NUM_INPUTS = CAMERA_CHANNELS.length;
/** `Gui::CoreImplUpdateInputStates`' hysteresis (0x3f666666, 0x3dcccccd) and
 * `CoreImplUpdateInput`'s change threshold on the squared difference (0x322bcc76). */
const ENTER = f(0.9), LEAVE = f(0.1), CHANGE_EPS2 = f32Bits(0x322bcc76);
/** `CameraMan::InputType::Value` (`m_lastInputType`, the index of `m_inputParameters`). */
const kMouse = 0, kStick = 1, kKeyboard = 2;
/** `ParseInput`'s constants: the stick dead zone (0x159bd28), the slow-axis limit (0x1590164),
 * the pointer delta's weights (0x15add60 = 0x3e4ccccc, 0x15a9a00), the speed ramp
 * (`m_CurrentCameraSpeedRatio` = 0.875·(1 - timer/1000) + 0.125: 0x15add5c, 0x15a9554, 0x15add58)
 * and the velocity's rest threshold (0x15add54). */
const DEADZONE = f(0.2), SLOW = f(0.5), DELTA_NEW = f32Bits(0x3e4ccccc), DELTA_LAST = f(0.8);
const RAMP_SPAN = f(0.875), RAMP_TIME = f(1000), RAMP_MIN = f(0.125), REST = f(1e-4);

export const CAM_TICK = 0, CAM_INTRO = 1, CAM_ENTER_TURN = 2, CAM_ENTER_CRATE_DROP = 3, CAM_CENTRE = 4,
  CAM_TRACK_NOW = 5, CAM_SHAKE = 6, CAM_SHAKE_DEFAULT = 7, CAM_STOP_SHAKE = 8, CAM_PULSE = 9,
  CAM_ZOOM_OUT_MAX = 10, CAM_ZOOM_LOOK = 11, CAM_TRACKING_MODE = 12, CAM_LOOK_AT = 13, CAM_REPLAY_KICK = 14, CAM_REPLAY_DONE = 15,
  CAM_SUDDEN_DEATH = 16, CAM_POST_EFFECT = 17, CAM_GADGET_EFFECT = 18, CAM_GAME_START = 19, CAM_INIT_TURN = 20;
export const DMG_CLEAR = 0, DMG_BLAST = 1, DMG_GIRDER = 2, DMG_OMG = 3, DMG_OMG_OCCLUDER = 4;

/** A damage-pass entry of a step (`DMG_*`, crates/sim/src/view/camera.rs). */
export type DamageEntry = { kind: number; args: number[] };

/** What the renderer draws with: the pushed game camera (position and look-at with noise) and
 * the background layer camera's distance (`UpdateGraphicCameras` 0xb61280). */
export type CameraPose = { x: number; y: number; z: number; lookX: number; lookY: number; lookZ: number; bgZ: number; zoomParam: number };

type Tracked = { idx: number; sub: number; interest: number; manual: number; birth: number; x: number; y: number; crate: boolean; noComment: boolean };

/** What `ParseInput` reads from logic (crates/sim/src/view/camera.rs `parse_input_tail`). */
type CentreInput = {
  team: number; flags: number; wormTeam: number; x: number; y: number; key: number; cx: number; cy: number;
};

/** The tick input (crates/sim/src/view/camera.rs `tick_input`); `endZoom`, `endCustom`: the
 * intro's end zoom type and custom zoom (`place`). */
type TickInput = {
  time: number; flags: number; introFinish: number; introDuration: number;
  bl: [number, number]; tr: [number, number]; water: number; land: [number, number, number, number]; landScaleX: number;
  objects: Tracked[]; centre: CentreInput; endZoom: number; endCustom: number;
};

/** `CameraMan::m_Ld.m_Input` (22 channels, `CAMERA_CHANNELS`) and the camera node's side of the
 * Gui that fills it. Each step `pump` runs `LogicalGuiInput::Pump`'s event -1 (every state
 * `&= 7`: the edge bits go) and then the events `Gui::CoreImplUpdateInput` 0xd81a50 makes of
 * the new values, which `CameraMan::OnLogicalInput` 0xb5f950 stores: 13 the value; 14 (state
 * 0 → +1) `& 0xfc | 0x84`; 16 (→ -1) `& 0xf9 | 0x21`; 15 (→ 0) `& 0xfa | 0x42`, then 17 (from +1)
 * `| 0x10` or 18 (from -1) `| 0x08`; each also stores the value. So `states & 5` is "held" and
 * `states & 0xa0` "pressed this step". The Gui's states follow `CoreImplUpdateInputStates`'
 * hysteresis (enter at ±0.9, leave at ±0.1); continuous channels only send 13. */
export class CameraInput {
  readonly values = new Float32Array(NUM_INPUTS);
  readonly states = new Uint8Array(NUM_INPUTS);
  readonly filter = new Uint8Array([0xff, 0xff, 0xff]);
  /** `m_ResetValues`: the normalised pointer 0.5 (`Initialize` 0xb647b0), the rest 0. */
  private readonly resetValues = new Float32Array(NUM_INPUTS);
  /** The Gui user's state (-1, 0, +1) and value of each channel at the last pump. */
  private readonly guiState = new Int8Array(NUM_INPUTS);
  private readonly guiValue = new Float32Array(NUM_INPUTS);

  constructor() {
    this.resetValues[10] = 0.5;
    this.resetValues[11] = 0.5;
    this.values.set(this.resetValues);
  }

  /** One step's values of the channels (`CAMERA_CHANNELS` order). */
  pump(raw: Float32Array): void {
    for (let i = 0; i < NUM_INPUTS; i++) this.states[i] &= 7;
    for (let ch = 0; ch < NUM_INPUTS; ch++) {
      const v = f(raw[ch]), vp = this.guiValue[ch], sp = this.guiState[ch];
      const d = f(v - vp);
      const changed = f(d * d) >= CHANGE_EPS2;
      let s: number;
      if (sp === 1) s = -ENTER >= v ? -1 : LEAVE >= v ? 0 : 1;
      else if (sp === -1) s = v >= ENTER ? 1 : v >= -LEAVE ? 0 : -1;
      else s = -ENTER >= v ? -1 : v >= ENTER ? 1 : 0;
      this.guiState[ch] = s;
      this.guiValue[ch] = v;
      if (ch >= 10 && ch <= 13 || s === sp) {
        if (changed) this.values[ch] = v;
      } else if (s === 0) {
        this.states[ch] = (this.states[ch] & 0xfa) | 0x42 | (sp === 1 ? 0x10 : 0x08);
        this.values[ch] = v;
      } else {
        this.states[ch] = s === 1 ? (this.states[ch] & 0xfc) | 0x84 : (this.states[ch] & 0xf9) | 0x21;
        this.values[ch] = v;
      }
    }
  }

  /** `CameraMan::InitTurn` 0xb61860: states 0, values = reset values, filter ff ff ff. */
  initTurn(): void {
    this.states.fill(0);
    this.values.set(this.resetValues);
    this.filter.fill(0xff);
  }

  /** `CameraMan::ResetButtonInputChannels` 0xb61150: every value and state 0, every channel's
   * filter bit set. */
  resetButtons(): void {
    this.values.fill(0);
    this.states.fill(0);
    this.filter[0] = 0xff;
    this.filter[1] = 0xff;
    this.filter[2] |= 0x3f;
  }
}

// Enums (gen/enums.rs).
const kInterest_None = 2, kInterest_Max = 12;
const kOff = 0, kRequestingOn = 1, kOn = 2;
const kAverage = 0, kMostRecent = 1, kNoTracking = 2;
const kLockToActivity = 0, kKeepWithinBorder = 1, kManual = 2;
const kInactive = 0, kShaking = 1, kFading = 2;
const kZoomClose = 0, kZoomFar = 1, kZoomCustom = 2, kZoomDeathMatch = 3;

/** One axis of `CameraMan::CubicInterpolationAnimation`, in float32 operation for operation as
 * the SSE code (a 1-ulp different position rounds the camera to the other integer: animals-A
 * 2121 z 5334 / 5333, 2160 y -1235 / -1234 in double). */
class Cubic {
  active = false; startPosition = 0; endPosition = 0; position = 0; time = 0; duration = 0;
  startSpeed = 0; alpha = 0; beta = 0;

  /** `Initialise` 0xb638f0: a running animation hands its current speed on. */
  init(start: number, end: number, duration: number, velocity: number): void {
    const t = this.time;
    const v = this.active
      ? f(f(f(f(t * t) * f(this.alpha * 3)) + f(t * f(this.beta + this.beta))) + this.startSpeed)
      : f(velocity * DT);
    this.startSpeed = v;
    const d = f(start - end), dd = f(duration * duration);
    this.startPosition = start; this.time = 0; this.position = start; this.duration = duration;
    this.active = true; this.endPosition = end;
    this.beta = f(-f(f(3 * d) + f(f(v + v) * duration)) / dd);
    this.alpha = f(f(f(v * duration) + f(d + d)) / f(dd * duration));
  }

  /** `Update` 0xb641d0. */
  update(dt: number): void {
    const t = Math.max(0, Math.min(f(dt + this.time), this.duration));
    this.time = t;
    if (t < this.duration) {
      const t2 = f(t * t);
      this.position = f(f(f(t * this.startSpeed) + f(f(t2 * this.beta) + f(f(t * t2) * this.alpha))) + this.startPosition);
    } else { this.active = false; this.position = this.endPosition; }
  }
}

/** Hugo Elias' integer noise (`LogicalUpdateCameraShake`): 1 − hash/2³⁰ in (−1, 1]. */
function noise(n: number): number {
  const m = ((n << 13) ^ n) >>> 0;
  const h = (Math.imul(m, (Math.imul(Math.imul(m, m), 0x3d73) + 0xc0ae5) | 0) + 0x5208dd0d) & 0x7fffffff;
  return f(1 - f(h * 9.313225746154785e-10));
}

/** The cosine interpolation weight of the shake noise (polynomial cos(πt) ≈, 0x15add4c/0x15add50). */
function cosw(t: number): number {
  if (t < 0.5) {
    const s = f(t * Math.PI * Math.PI * t);
    return f(s * (s * 0.03705 - 0.4967) + 1);
  }
  const u = f((1 - t) * Math.PI);
  const s = f(u * u);
  return -f(s * (s * 0.03705 - 0.4967) + 1);
}

/** 1-D value noise at x (the shake's second term). */
function valueNoise(x: number): number {
  const i = Math.trunc(x) >>> 0;
  const t = f(x - Math.trunc(x));
  const w = f((1 - cosw(t)) * 0.5);
  return f(noise(i) * (1 - w) + noise((i + 1) >>> 0) * w);
}

export class CameraMan {
  // --- options (`Initialize` 0xb647b0; GameOptions `Camera.*` sent with every view) ---------------
  private opt: number[] = [];
  /** Render target size the frustum and `m_zoom.min` depend on (`SetupCameraForRound`). */
  screenW = 1280;
  aspect = 16 / 9;

  // --- state --------------------------------------------------------------------------------------
  pos = { x: 0, y: 0, z: 0 };
  private lookAt = { x: 0, y: 0, z: 0 };
  private desired = { x: 0, y: 0, z: 0 };
  private immediate = { x: 0, y: 0 };
  private velocity = { x: 0, y: 0 };
  private desiredVelocity = { x: 0, y: 0 };
  private noiseOff = { x: 0, y: 0, z: 0 };
  private centreTarget = { x: 0, y: 0 };
  private centrePos = { x: 0, y: 0 };
  private borderTarget = { x: 0, y: 0 };
  private borderNudge = { x: 0, y: 0 };
  private bl = { x: 0, y: 0 };
  private tr = { x: 0, y: 0 };
  private zoomMin = 0;
  private zoomMax = 22500;
  private maxUpperLimit = 0;
  private maxSpeed = 1e16;
  private animActive = kOff;
  private allowUpdate = true;
  private introActive = false;
  private introPaused = false;
  private holdLeft = 0;
  private lateralX = new Cubic();
  private lateralY = new Cubic();
  private zoomAnim = new Cubic();
  private cameraMoved = false;
  private graphicMoved = false;
  private trackingMode = kMostRecent;
  private cameraMode = kKeepWithinBorder;
  /** `Replay::g_vOriginalCameraPosition` while `g_bSetOriginalCameraPosition` (null: unset). */
  private replaySaved: { x: number; y: number } | null = null;
  /** The post-processing events of the stepped views (`CAM_SUDDEN_DEATH`, `CAM_POST_EFFECT` and
   * `CAM_GADGET_EFFECT` with their arguments), in order; the renderer drains them. */
  readonly postEvents: { kind: number; args: number[] }[] = [];
  /** The landscape records of the stepped views (buildings.ts), in order; the renderer drains
   * them. */
  readonly landscape: Uint8Array[] = [];
  private timeTracking = 1000;
  private trackedId = 0xffffffff;
  private trackedInterest = kInterest_None;
  private manualOverride = -1;
  private ignoreCrates = false;
  private usingTargeted = false;
  private mouseActive = false;
  /** `m_PadActive`: the stick or a camera key moved the camera this tick (keeps it `kManual`). */
  private padActive = false;
  private removeManual = false;
  private secondsWithoutManual = 0;
  /** `m_InputAllowed` (`Initialize`: `kOn`). */
  private inputAllowed = kOn;
  /** `m_IgnoreInputTime`: `OnGameStart` sets `IntroCameraInputIgnoreTime`, each tick takes one
   * `kLogicUpdate` off; the player's camera input counts only once it is no longer positive. */
  private ignoreInputTime = 0;
  /** `m_lastInputType` (`Initialize`: `kMouse`). */
  private lastInputType = kMouse;
  /** `m_LastPointerDelta`: written only on a `kRequestingOn` tick, from the values that tick
   * zeroed, so it stays (0, 0). */
  private lastPointerDelta = { x: 0, y: 0 };
  /** The speed ramp of the stick and camera keys: `m_CameraMoveFastTimer`, `m_LastTimeRef` (wall
   * clock, `XomGetSystemTimeSecs`), `m_MoveSpeedDir`, `m_CurrentCameraSpeedRatio`. */
  private moveFastTimer = 0;
  private lastTimeRef = 0;
  private moveSpeedDir = 0;
  private speedRatio = 1;
  /** `m_currentBuildingKey` (not initialised by the original) and `m_currentBuildingCentre`. */
  private currentBuildingKey = 0xcdcdcdcd;
  private currentBuildingCentre = { x: 0, y: 0 };
  /** The camera node's input block (`m_Ld.m_Input`, the client's copy with the local channels). */
  readonly in = new CameraInput();
  private raw = new Float32Array(NUM_INPUTS);
  /** The local player's channel values, sampled once per step (input.ts `Binder.cameraInput`):
   * `UserMan::Pump` and `Gui::Pump` of the step. Null: no input (node tests, replays). */
  readInput: ((out: Float32Array) => void) | null = null;
  /** `NetGame::IsLocalMenuOpen` of the menus that stop the camera input (`kMenu_Pause`,
   * `kMenu_RoundResults`, `kMenu_Chat`; the NUI invite dialog) and of the weapons panel
   * (`kMenu_WeaponsPanel`, which only stops the mouse). Set by the match screen. */
  menuOpen = false;
  panelOpen = false;
  /** `NetSetup::IsLocalWorm`/`NetGame::IsInPlayRivalLocal` of a network match: whether a team is
   * this client's (null: none is). */
  localTeam: ((team: number) => boolean) | null = null;
  /** Wall clock in ms (`XomGetSystemTimeMilli`; tests may replace it). */
  clock: () => number = () => performance.now();
  private clockStart = -1;
  private borderScale = 1;
  private shake = { state: kInactive, magX: 0, magY: 0, freqX: 0, freqY: 0, duration: 0, fade: 0, timeActive: 0, timeFading: 0 };
  private pulse = 0;
  private input: TickInput | null = null;
  /** The pose last pushed to the graphics (`UpdateGraphicCameras`); tracking's `IsOffscreen` and
   * the activity border read it. */
  private pushed: CameraPose = { x: 0, y: 0, z: 1, lookX: 0, lookY: 0, lookZ: 0, bgZ: 1, zoomParam: 0 };
  /** The pose a presented frame shows: the push of the step before the frame's. The original
   * presents frame t before `CameraMan::GraphicUpdate` 0xb616e0 pushes tick t's camera (gdb on
   * strikes-objects-A 3498-3501: TaskMan::LogicUpdate t, CameraMan::LogicUpdate, the swap of
   * frame t, then GraphicUpdate/UpdateGraphicCameras with tick t's position), so frame t is
   * drawn with tick t - 1's camera while the objects are at tick t (sx 3500: 288, not 279). */
  private shown: CameraPose = this.pushed;
  started = false;
  /** The camera was given its round's position: `CAM_INTRO`, or `place` when the client never
   * saw it (a state snapshot join) or its views jumped (a restore). */
  private placed = false;
  /** The tick of the last stepped view (-1: none). */
  private lastTick = -1;

  private o(i: number, d: number): number {
    const v = this.opt[i];
    return v === undefined || !Number.isFinite(v) ? d : v;
  }

  private get fy(): number { return FRUSTUM_X / this.aspect; }

  pose(): CameraPose { return this.shown; }

  /** `m_bWormUsingTargetedWeapon`: this client's worm aims a targeted weapon. */
  get targeting(): boolean { return this.usingTargeted; }

  /** `GetCameraBounds`: the round bounds (`m_BoundsBottomLeft`, `m_BoundsTopRight`). */
  bounds(): { bl: { x: number; y: number }; tr: { x: number; y: number } } { return { bl: this.bl, tr: this.tr }; }

  /** One sim step's camera view; returns its damage-pass entries. The step starts with its input
   * (`TaskMan::LogicUpdate`: `UserMan::Pump`, `LogicalGuiInput::Pump`, `Gui::Pump` precede the
   * walk). */
  step(bytes: Uint8Array): DamageEntry[] {
    this.shown = this.pushed;
    this.readInput?.(this.raw);
    this.in.pump(this.raw);
    const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    let p = 0;
    if (String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]) !== 'WMC1') throw new Error('camera view: bad magic');
    // Every step's view arrives in order (fast-forwards included); a jump is a restored sim (a
    // resync's state snapshot), whose camera history this camera never followed.
    const tick = dv.getUint32(4, true);
    if (this.lastTick >= 0 && tick !== this.lastTick + 1) this.placed = false;
    this.lastTick = tick;
    p = 8;
    const nOpt = bytes[p++];
    const opt: number[] = [];
    for (let i = 0; i < nOpt; i++, p += 4) opt.push(dv.getFloat32(p, true));
    if (!this.opt.length) this.initialise(opt);
    const nEv = dv.getUint16(p, true); p += 2;
    const events: { kind: number; args: number[] }[] = [];
    for (let i = 0; i < nEv; i++) {
      const kind = bytes[p++], n = bytes[p++];
      const args: number[] = [];
      for (let k = 0; k < n; k++, p += 4) args.push(dv.getFloat32(p, true));
      while (args.length < 10) args.push(0);
      events.push({ kind, args });
    }
    const len = dv.getUint32(p, true); p += 4;
    if (len) this.input = decodeTickInput(dv, p, p + len);
    p += len;
    // The step's landscape records (crates/sim/src/view/landscape.rs), for buildings.ts.
    if (p + 4 <= bytes.length) {
      const n = dv.getUint32(p, true); p += 4;
      if (n) this.landscape.push(bytes.subarray(p, p + n));
    }
    const inp = this.input;
    if (inp) {
      this.bl = { x: inp.bl[0], y: inp.bl[1] };
      this.tr = { x: inp.tr[0], y: inp.tr[1] };
      this.setupZoom(inp.landScaleX);
      this.introPaused = (inp.flags & 8) !== 0;
    }
    const damage: DamageEntry[] = [];
    for (const e of events) {
      if (e.kind & 0x80) { damage.push({ kind: e.kind & 0x7f, args: e.args }); continue; }
      this.apply(e.kind, e.args);
    }
    this.graphicUpdate();
    return damage;
  }

  private initialise(opt: number[]): void {
    this.opt = opt;
    this.zoomMax = this.o(1, 22500);
    this.maxSpeed = 1e16;
    this.desired = { x: 0, y: 0, z: this.zoomMax };
    this.shake.magX = this.o(18, 10); this.shake.magY = this.o(19, 5);
    this.shake.freqX = this.o(20, 32); this.shake.freqY = this.o(21, 16);
    this.shake.duration = this.o(22, 10); this.shake.fade = this.o(23, 1);
    this.started = true;
  }

  /** `SetupCameraForRound` 0xb64570 / `SetCameraBounds` 0xb60400: `m_zoom.min` = screen width ·
   * landscape scale · 0.5 / tan(m_FrustumXSize) and the background camera's upper limit. */
  private setupZoom(scaleX: number): void {
    this.zoomMin = f((this.screenW * scaleX * 0.5) / Math.tan(FRUSTUM_X));
    const mx = this.o(40, 0) || 4000, my = this.o(41, 0) || 2000;
    this.maxUpperLimit = Math.max(mx / (2 * FRUSTUM_X), my / (2 * this.fy));
  }

  private zoomFromEnum(e: number): number {
    if (e === kZoomClose) return this.zoomMin;
    if (e === kZoomDeathMatch) return f((this.zoomMax - this.zoomMin) * this.o(2, 0) + this.zoomMin);
    return this.zoomMax;
  }

  private apply(kind: number, a: number[]): void {
    switch (kind) {
      case CAM_TICK: this.logicUpdate(); break;
      case CAM_INTRO: this.setIntro(a); break;
      case CAM_ENTER_TURN:
        if (this.trackingMode === kNoTracking) this.trackingMode = kMostRecent;
        if (this.cameraMode !== kManual) { this.timeTracking = 1000; this.trackedId = 0xffffffff; this.manualOverride = -1; }
        break;
      case CAM_ENTER_CRATE_DROP: this.ignoreCrates = false; break;
      case CAM_CENTRE: this.centre(); break;
      case CAM_TRACK_NOW: this.timeTracking = 1000; break;
      case CAM_SHAKE:
        // Machine-gun explosions shake only the in-play rival's screen (ExplosionMan::Explosion).
        if (a[6] && !this.rivalLocal()) break;
        this.startShake(a[0], a[1], a[2], a[3], a[4], a[5]);
        break;
      case CAM_SHAKE_DEFAULT: this.startShake(this.o(18, 10), this.o(19, 5), this.o(20, 32), this.o(21, 16), this.o(22, 10), this.o(23, 1)); break;
      case CAM_STOP_SHAKE: this.shake.state = kFading; this.shake.timeFading = 0; break;
      case CAM_PULSE: this.pulse = a[0]; break;
      case CAM_ZOOM_OUT_MAX: {
        this.initAnimation({ ...this.pos }, { x: this.pos.x, y: this.pos.y, z: this.zoomFromEnum(kZoomFar) }, a[0], 0);
        this.lateralX.active = false; this.lateralY.active = false;
        break;
      }
      case CAM_ZOOM_LOOK:
        this.velocity = { x: 0, y: 0 };
        this.initAnimation({ ...this.pos }, { x: a[1], y: a[2], z: this.zoomFromEnum(a[3]) }, a[0], 0);
        break;
      case CAM_TRACKING_MODE: this.trackingMode = a[0]; break;
      case CAM_LOOK_AT: this.lookAtImmediate(a[0], a[1]); break;
      // Replay::Kick saves the position in kManual; Replay::Done3 looks at it again.
      case CAM_REPLAY_KICK: if (this.cameraMode === kManual) this.replaySaved = { x: this.pos.x, y: this.pos.y }; break;
      case CAM_REPLAY_DONE: if (this.replaySaved) { this.lookAtImmediate(this.replaySaved.x, this.replaySaved.y); this.replaySaved = null; } break;
      case CAM_SUDDEN_DEATH: case CAM_POST_EFFECT: case CAM_GADGET_EFFECT: this.postEvents.push({ kind, args: a }); break;
      case CAM_GAME_START: this.ignoreInputTime = this.o(37, 0); break;
      case CAM_INIT_TURN: this.in.initTurn(); break;
    }
  }

  /** `NetGame::IsInPlayRivalLocal` (`g_InPlayRivalIsLocal`, `NetGame::EnterTurn` 0xc4b1c0: the
   * turn's rival is this machine's): in a network match the current team is this client's; a
   * local match has no remote rival. */
  private rivalLocal(): boolean {
    const inp = this.input;
    if (!inp || (inp.flags & 32) === 0) return true;
    return this.localTeam?.(inp.centre.team) ?? false;
  }

  private startShake(mx: number, my: number, fx: number, fy: number, dur: number, fade: number): void {
    Object.assign(this.shake, { magX: mx, magY: my, freqX: fx, freqY: fy, duration: dur, fade, state: kShaking, timeActive: 0, timeFading: 0 });
  }

  /** `CentreActivityTarget` 0xb617d0: lock to the activity from the current position. */
  private centre(): void {
    this.cameraMode = kLockToActivity;
    this.centrePos = { x: this.pos.x, y: this.pos.y };
  }

  /** `CentreActivityTarget` from `WormHUDPanel::UpdateTransitionState` 0xbfe680 (HUD code; hud.ts
   * calls it after the tick's camera step): the current worm's arrow switched on, the camera is
   * not manual. */
  hudCentre(): void {
    if (this.cameraMode !== kManual) this.centre();
  }

  private setAnimActive(on: boolean): void {
    if (on) { if (this.animActive !== kRequestingOn && this.animActive !== kOn) this.animActive = kRequestingOn; }
    else if (this.animActive === kOn) { this.animActive = kOff; this.introActive = false; }
  }

  /** `SetIntroCameraAnimation` 0xb63b10 (arguments as the sim's `CAM_INTRO`). */
  private setIntro(a: number[]): void {
    const [sx, sy, startZoom, hold, ex, ey, endZoom, duration, custom, paused] = a;
    const endZ = endZoom === kZoomCustom ? Math.min(this.zoomMax, Math.max(this.zoomMin, custom)) : this.zoomFromEnum(endZoom);
    const startZ = this.zoomFromEnum(startZoom);
    this.placed = true;
    this.setAnimActive(true);
    this.introActive = true;
    this.introPaused = paused !== 0;
    const start = { x: sx, y: sy, z: startZ };
    this.setCurrent(start);
    this.centreTarget = { x: ex, y: ey };
    this.push();
    this.initAnimation(start, { x: ex, y: ey, z: endZ }, duration, hold);
  }

  /** The camera of a client that never saw its round's `CAM_INTRO` (it joined from a state
   * snapshot) or whose views jumped (a resync's restore): no original counterpart, so it starts
   * as the intro leaves the camera. `SetIntroCameraAnimation` 0xb63b10 ends at its end zoom
   * (`MatchSpec::GetCameraEndZoom`, from the tick input) on `TeamLogic::CalcNextTurn`'s worm:
   * here the current worm, else the most interesting, most recent activity, else the round
   * bounds' centre. Then `CentreActivityTarget`, so tracking takes the camera to the activity as
   * at a turn start. Without a round (no bounds yet) it waits. */
  private place(): void {
    const inp = this.input;
    if (!inp || (inp.bl[0] === 0 && inp.bl[1] === 0 && inp.tr[0] === 0 && inp.tr[1] === 0)) return;
    this.placed = true;
    let x = f((inp.bl[0] + inp.tr[0]) * 0.5), y = f((inp.bl[1] + inp.tr[1]) * 0.5);
    if ((inp.centre.flags & 1) !== 0) { x = inp.centre.x; y = inp.centre.y; }
    else {
      let best: Tracked | null = null;
      for (const o of inp.objects) {
        if (o.interest > kInterest_None && o.interest <= kInterest_Max && (!best || o.interest > best.interest || (o.interest === best.interest && o.birth > best.birth))) best = o;
      }
      if (best) { x = best.x; y = best.y; }
    }
    const z = inp.endZoom === kZoomCustom ? Math.min(this.zoomMax, Math.max(this.zoomMin, inp.endCustom)) : this.zoomFromEnum(inp.endZoom);
    this.lateralX.active = false; this.lateralY.active = false; this.zoomAnim.active = false;
    this.animActive = kOff;
    this.introActive = false;
    this.holdLeft = 0;
    this.velocity = { x: 0, y: 0 };
    this.desiredVelocity = { x: 0, y: 0 };
    this.shake.state = kInactive;
    this.pulse = 0;
    this.replaySaved = null;
    this.trackedId = 0xffffffff;
    this.trackedInterest = kInterest_None;
    this.timeTracking = 1000;
    this.manualOverride = -1;
    this.ignoreCrates = false;
    this.mouseActive = false;
    this.padActive = false;
    this.secondsWithoutManual = 0;
    this.in.initTurn();
    this.setCurrent({ x, y, z });
    this.desired = { ...this.pos };
    this.immediate = { x: this.pos.x, y: this.pos.y };
    this.centreTarget = { x: this.pos.x, y: this.pos.y };
    this.noiseOff = { x: 0, y: 0, z: 0 };
    this.push();
    this.centre();
  }

  /** `InitialiseAnimation` 0xb639c0. */
  private initAnimation(s: { x: number; y: number; z: number }, e: { x: number; y: number; z: number }, time: number, hold: number): void {
    const dx = f(e.x - s.x), dy = f(e.y - s.y), dz = f(e.z - s.z);
    if (!(f(0.1) > f(f(f(dy * dy) + f(dx * dx)) + f(dz * dz)))) {
      this.lateralX.init(s.x, e.x, time, this.velocity.x);
      this.lateralY.init(s.y, e.y, time, this.velocity.y);
      this.zoomAnim.init(s.z, e.z, time, 0);
      if (hold > 0) this.holdLeft = f(hold + this.o(38, 2));
      this.setAnimActive(true);
      this.setInputAllowed(true);
      return;
    }
    this.setCurrent(e);
    this.desired = { ...e };
    this.cameraMoved = true;
    this.introActive = false;
  }

  /** `SetCurrentCameraPosition` 0xb60f20. */
  private setCurrent(c: { x: number; y: number; z: number }): void {
    const p = { ...c };
    this.clamp(p);
    this.pos = p;
    this.lookAt = { x: p.x, y: f(p.y + this.o(8, 0)), z: 0 };
    this.cameraMoved = true;
  }

  /** `LookAtPositionImmediate` 0xb61010; the distance in float32 as the original (a 1-ulp
   * different target decides whether the camera steps: animals-A 3608). */
  private lookAtImmediate(x: number, y: number): void {
    if (this.animActive === kOn || this.mouseActive) { this.immediate = { x, y }; return; }
    const dx = f(x - this.immediate.x), dy = f(y - this.immediate.y);
    if (!(1 <= f(Math.sqrt(f(f(dy * dy) + f(dx * dx)))))) return;
    this.immediate = { x, y };
    const p = { x, y, z: this.pos.z };
    this.clamp(p);
    this.setCurrent(p);
    this.velocity = { x: 0, y: 0 };
    this.desiredVelocity = { x: 0, y: 0 };
    this.desired = { ...p };
  }

  /** World → normalised screen through the last pushed camera (`CoordinateTransformation::
   * WorldToNormalised` 0xc6bdd0; the game camera looks straight down the z axis). */
  private toNormalised(x: number, y: number): [number, number] {
    const c = this.pushed;
    return [0.5 + (x - c.x) / (2 * c.z * FRUSTUM_X), 0.5 + (y - c.y) / (2 * c.z * this.fy)];
  }

  /** `CoordinateTransformation::NormalisedToWorld` 0xc6bb10 in float32, operation for operation:
   * the ray through the pushed camera's frustum (left/right ±FRUSTUM_X, bottom/top ±fy at near
   * 1) along the normalised look direction, cut with z = 0. */
  private toWorld(nx: number, ny: number): [number, number] {
    const c = this.pushed;
    let dx = f(c.lookX - c.x), dy = f(c.lookY - c.y), dz = f(c.lookZ - c.z);
    const len2 = f(f(f(dy * dy) + f(dx * dx)) + f(dz * dz));
    if (EPS < len2) {
      const inv = f(1 / f(Math.sqrt(len2)));
      dx = f(dx * inv); dy = f(dy * inv); dz = f(dz * inv);
    }
    const left = f(-FRUSTUM_X), right = f(FRUSTUM_X), bottom = f(-this.fy), top = f(this.fy);
    const alpha = f(f(f(right - left) * nx) + left), beta = f(f(f(top - bottom) * ny) + bottom);
    const vx = f(f(dx + alpha) + f(beta * 0)), vy = f(f(dy + f(alpha * 0)) - f(dz * beta)), vz = f(f(f(alpha * 0) + dz) + f(beta * dy));
    const lambda = f(-c.z / vz);
    return [f(c.x + f(vx * lambda)), f(f(lambda * vy) + c.y)];
  }

  /** `CameraMan::IsOffscreen` 0xb62250: the point's normalised screen position outside [0, 1]²
   * (also `WormHUDActivity::GraphicUpdate`'s test, game/widgets.ts). */
  isOffscreen(x: number, y: number): boolean {
    const [nx, ny] = this.toNormalised(x, y);
    return nx < 0 || nx > 1 || ny < 0 || ny > 1;
  }

  /** `CameraActivityBorder::GetExtents` 0xbdf100 at the border scale (the normalised zoom), in
   * float32; the extents are the options × 0.01f (`CameraMan::Initialize`). */
  private borderExtents(): [number, number] {
    const s = this.borderScale, c = f(0.01);
    const minH = f(this.o(10, 50) * c), minV = f(this.o(11, 40) * c), maxH = f(this.o(12, 70) * c), maxV = f(this.o(13, 60) * c);
    return [f(minH + f(s * f(maxH - minH))), f(f(f(maxV - minV) * s) + minV)];
  }

  /** `CameraActivityBorder::GetCorners` 0xbdf450: the border's normalised left and bottom
   * (right and top are 1 minus them). */
  private borderCorners(): [number, number] {
    const [ex, ey] = this.borderExtents();
    return [f(f(1 - ex) * 0.5), f(0.5 * f(1 - ey))];
  }

  /** `CameraActivityBorder::Contains` 0xbdf1a0. */
  private borderContains(x: number, y: number): boolean {
    const [nx, ny] = this.toNormalised(x, y);
    const [mx, my] = this.borderCorners();
    return mx <= nx && nx <= f(1 - mx) && my <= ny && ny <= f(1 - my);
  }

  /** `CameraActivityBorder::GetRectilinearDistance` 0xbdf990 (GetWorldCorners 0xbdf510). */
  private borderDistance(x: number, y: number): [number, number] {
    const [mx, my] = this.borderCorners();
    const [left, top] = this.toWorld(mx, f(1 - my));
    const [right, bottom] = this.toWorld(f(1 - mx), my);
    const dx = right < x ? f(x - right) : x < left ? f(x - left) : 0;
    const dy = top < y ? f(y - top) : y < bottom ? f(y - bottom) : 0;
    return [dx, dy];
  }

  /** `CameraMan::LogicUpdate` 0xb660e0 (the sim ran `ParseInput`'s hashed part). */
  private logicUpdate(): void {
    if (!this.placed) this.place();
    const inp = this.input;
    // UpdateTargetingFlags 0xb64450 (the logical part comes with the tick input): in a network
    // match only the in-play rival's machine holds the flag.
    this.usingTargeted = !!inp && (inp.flags & 2) !== 0 && this.animActive !== kRequestingOn && this.animActive !== kOn && this.rivalLocal();
    if (this.animActive === kRequestingOn || this.animActive === kOn) this.updateAnimation();
    else this.updateTracking();
    this.ignoreInputTime = f(this.ignoreInputTime - DT);
    this.parseInput();
    this.allowUpdate = false;
    if (this.cameraMoved) {
      this.cameraMoved = false;
      this.graphicMoved = true;
      const close = this.o(14, 28000), far = this.o(15, 210000);
      this.maxSpeed = f(((this.pos.z - this.zoomMin) / (this.zoomMax - this.zoomMin)) * (far - close) + close);
    }
    if (!this.mouseActive && !this.padActive) this.secondsWithoutManual = f(DT + this.secondsWithoutManual);
    else this.secondsWithoutManual = 0;
    this.updateShake();
    const n = this.noiseOff;
    if (n.x * n.x + n.y * n.y + n.z * n.z > 0) this.graphicMoved = true;
  }

  /** `SetInputAllowed` 0xb61750. */
  private setInputAllowed(on: boolean): void {
    if (on) { if (this.inputAllowed !== kRequestingOn && this.inputAllowed !== kOn) this.inputAllowed = kRequestingOn; }
    else this.inputAllowed = kOff;
  }

  /** `CancelIntroCamera` 0xb62030: in a network match or while the replay file plays only an
   * unpaused intro stops. */
  private cancelIntro(): void {
    const flags = this.input?.flags ?? 0;
    if (((flags & 32) === 0 && (flags & 64) === 0) || !this.introPaused) {
      this.lateralX.active = false; this.lateralY.active = false; this.zoomAnim.active = false;
      this.introActive = false;
      this.introPaused = false;
    }
  }

  /** `XomGetSystemTimeSecs` 0x126d7b0: the ms since the first call (u32) as float32 × 0.001f. */
  private systemTimeSecs(): number {
    const now = this.clock();
    if (this.clockStart < 0) this.clockStart = now;
    return f(f(Math.trunc(now - this.clockStart) >>> 0) * f(0.001));
  }

  /** `ParseInput` 0xb651e0, from the disassembly: the player's camera control. Channels by block
   * index (`CAMERA_CHANNELS`); a channel counts only while its `m_Filter` bit is set.
   * - The input stops (`SetInputAllowed(false)`: velocities 0, `SetAnimActive(false)`) while a
   *   local menu is open; it resumes through `kRequestingOn`, whose tick zeroes the values.
   * - `AnyInput` pressed cancels the intro once `m_IgnoreInputTime` ran out.
   * - Keys and stick: `CameraTrack*` (unbound on PC) move at the input type's `MoveSpeed`; the
   *   stick (`CameraAxisX/Y` past 0.2) and the camera keys (`CameraLeft/Right/Up/Down`, NumPad
   *   4/6/8/2; one of a pair held is a full axis) move at `StickPC.MoveSpeed` × the speed ratio,
   *   and keep the camera manual (`m_PadActive`). The ratio is a wall-clock ramp: while an axis
   *   is at least 0.5 a 1000-"ms" timer counts down in seconds (`XomGetSystemTimeSecs`), so the
   *   ratio 0.875·(1 - timer/1000) + 0.125 creeps up from 0.125 by 0.000875 a second, and it
   *   counts back up when the axes rest. Accelerations are the last input type's.
   * - Mouse: the pointer delta of the step × 0.19999999 (+ 0.8 × `m_LastPointerDelta`, which is
   *   0) moves at `Mouse.MoveSpeed` (y inverted: the delta is screen-down) unless the worm
   *   targets or the weapons panel or chat is open; a move of at least 1 (5 px) makes the
   *   camera manual (`m_MouseActive`), a smaller one moves it without.
   * - `CameraCentre` held looks at the current worm, or at its building's centre when the
   *   building hides it (not while teleporting worms in).
   * - `ParseZoomInput`; a zoom counts as mouse (or pad) activity.
   * - The velocity eases towards the target (accelerate below, decelerate above its length),
   *   scaled by `TrackMultiplier` × (z / `m_zoom.min` - 1) + 1, rests below 1e-4² and moves the
   *   camera (stopping a running animation, cancelling the intro); while
   *   `m_IgnoreInputTime > 0` or `kRequestingOn` the target is 0. */
  private parseInput(): void {
    const ci = this.in, st = ci.states, val = ci.values;
    let allowed: number;
    if (this.menuOpen) {
      this.setInputAllowed(false);
      allowed = this.inputAllowed;
    } else {
      this.setInputAllowed(true);
      allowed = this.inputAllowed;
      if (this.animActive === kRequestingOn) this.animActive = kOn;
    }
    if (allowed === kOff) {
      this.setInputAllowed(false);
      this.lastPointerDelta = { x: 0, y: 0 };
      this.desiredVelocity = { x: 0, y: 0 };
      this.velocity = { x: 0, y: 0 };
      this.setAnimActive(false);
      return;
    }
    if (this.ignoreInputTime <= 0 && this.introActive && (ci.filter[2] & 0x20) !== 0 && (st[21] & 0xa0) !== 0) {
      this.cancelIntro();
      allowed = this.inputAllowed;
    }
    if (allowed === kRequestingOn) val.fill(0);
    const f0 = ci.filter[0], f1 = ci.filter[1];
    // CameraTrack* (keyboard).
    let trackX = 0, trackY = 0, keys = false;
    if ((f0 & 1) && (st[0] & 5)) { this.lastInputType = kKeyboard; keys = true; trackX = -1; }
    if ((f0 & 2) && (st[1] & 5)) { this.lastInputType = kKeyboard; keys = true; trackX = 1; }
    if ((f0 & 4) && (st[2] & 5)) { this.lastInputType = kKeyboard; keys = true; trackY = 1; }
    if ((f0 & 8) && (st[3] & 5)) { this.lastInputType = kKeyboard; keys = true; trackY = -1; }
    // The stick, then the camera keys over it.
    let axisX = 0, axisY = 0, pad = false;
    if ((f0 & 0x10) && Math.abs(val[4]) > DEADZONE) { this.lastInputType = kStick; pad = true; axisX = val[4]; }
    if ((f0 & 0x20) && Math.abs(val[5]) > DEADZONE) { this.lastInputType = kStick; pad = true; axisY = val[5]; }
    const left = (f0 & 0x40) !== 0 && (st[6] & 5) !== 0, right = (f0 & 0x80) !== 0 && (st[7] & 5) !== 0;
    let slowX: boolean;
    if (left !== right) { this.lastInputType = kKeyboard; pad = true; axisX = left ? -1 : 1; slowX = false; }
    else slowX = SLOW > Math.abs(axisX);
    const up = (f1 & 1) !== 0 && (st[8] & 5) !== 0, down = (f1 & 2) !== 0 && (st[9] & 5) !== 0;
    let par: { moveSpeed: number; accel: number; decel: number };
    let active: boolean, slow: boolean;
    if (up !== down) {
      // A vertical camera key alone: the keyboard's parameters, never slow.
      this.lastInputType = kKeyboard;
      axisY = up ? 1 : -1;
      par = this.inputParams(kKeyboard);
      active = true; pad = true; slow = false;
    } else {
      par = this.inputParams(this.lastInputType);
      active = keys || pad;
      slow = slowX && SLOW > Math.abs(axisY);
    }
    let moveX = f(trackX * par.moveSpeed), moveY = f(par.moveSpeed * trackY);
    let accel = par.accel, decel = par.decel;
    // The speed ratio (wall clock).
    const now = this.systemTimeSecs();
    let ratio = this.speedRatio;
    const ramp = (t: number): number => f(f(f(1 - f(t / RAMP_TIME)) * RAMP_SPAN) + RAMP_MIN);
    if (slow) {
      if (this.moveSpeedDir === 0) { ratio = RAMP_MIN; this.speedRatio = ratio; }
      else if (this.moveSpeedDir === -1) {
        const t = this.moveFastTimer;
        ratio = ramp(t);
        this.speedRatio = ratio;
        if (RAMP_TIME > t) this.moveFastTimer = f(t + f(now - this.lastTimeRef));
        else this.moveSpeedDir = 0;
      } else if (this.moveSpeedDir === 1) this.moveSpeedDir = -1;
      else if (this.moveSpeedDir === 2) { this.moveFastTimer = 0; this.moveSpeedDir = -1; }
    } else if (this.moveSpeedDir === 0) { this.moveSpeedDir = 1; this.moveFastTimer = RAMP_TIME; }
    else if (this.moveSpeedDir === -1) this.moveSpeedDir = 1;
    else if (this.moveSpeedDir === 1) {
      const t = this.moveFastTimer;
      ratio = ramp(t);
      this.speedRatio = ratio;
      if (t > 0) this.moveFastTimer = f(t - f(now - this.lastTimeRef));
      else { this.moveSpeedDir = 2; ratio = 1; this.speedRatio = 1; }
    } else if (this.moveSpeedDir === 2) { ratio = 1; this.speedRatio = 1; }
    const stickSpeed = this.inputParams(kStick).moveSpeed;
    this.lastTimeRef = now;
    moveX = f(f(f(axisX * ratio) * stickSpeed) + moveX);
    moveY = f(f(f(ratio * axisY) * stickSpeed) + moveY);
    if (active) this.mouseActive = false;
    this.padActive = pad;
    // The mouse: the pointer delta (screen x right, y down).
    let rawY = 0, newY = 0, rawX = 0, newX = 0;
    if (f1 & 0x20) { rawY = val[13]; newY = f(DELTA_NEW * rawY); }
    if (f1 & 0x10) { rawX = val[12]; newX = f(DELTA_NEW * rawX); }
    if (this.inputAllowed === kRequestingOn) this.lastPointerDelta = { x: rawX, y: rawY };
    const sy = f(f(this.lastPointerDelta.y * DELTA_LAST) + newY), sx = f(f(DELTA_LAST * this.lastPointerDelta.x) + newX);
    let mouse = this.mouseActive;
    if (!this.usingTargeted && (sy !== 0 || sx !== 0)) {
      this.mouseActive = mouse = true;
      this.lastInputType = kMouse;
    }
    if (mouse) {
      if (this.panelOpen) this.mouseActive = false;
      else {
        const ms = this.inputParams(kMouse);
        moveX = f(ms.moveSpeed * sx);
        moveY = f(-ms.moveSpeed * sy);
        accel = ms.accel; decel = ms.decel;
        if (1 > f(Math.sqrt(f(f(sy * sy) + f(sx * sx))))) this.mouseActive = false;
      }
    }
    // CameraCentre: look at the current worm (or the building hiding it).
    const c = this.input?.centre;
    if ((ci.filter[2] & 0x10) !== 0 && (st[20] & 5) !== 0 && c && (c.flags & 2) === 0 && (c.flags & 1) !== 0) {
      // BuildingMan::IsWormVisible_Graphical with bNetGameRunning = MatchSpec::GetIsNetworkGame.
      const net = ((this.input?.flags ?? 0) & 32) !== 0;
      const visible = (c.flags & 16) === 0 && (net ? this.localTeam?.(c.wormTeam) ?? false : (c.flags & 8) !== 0);
      this.immediate = { x: 0, y: 0 };
      if ((c.flags & 4) !== 0 && !visible) {
        if (c.key !== this.currentBuildingKey) {
          this.currentBuildingKey = c.key;
          this.currentBuildingCentre = { x: c.cx, y: c.cy };
        }
        this.lookAtImmediate(this.currentBuildingCentre.x, this.currentBuildingCentre.y);
      } else this.lookAtImmediate(c.x, c.y);
    }
    if (this.parseZoomInput()) {
      if (this.lastInputType === kMouse || this.lastInputType === kKeyboard) this.mouseActive = true;
      else this.padActive = true;
    }
    if (this.allowUpdate) {
      const cam = { ...this.pos };
      let dx = 0, dy = 0, want2 = 0;
      if (this.inputAllowed !== kRequestingOn && !(this.ignoreInputTime > 0)) {
        const k = f(f(this.o(3, 0) * f(f(cam.z / this.zoomMin) - 1)) + 1);
        dx = f(moveX * k);
        dy = f(k * moveY);
        want2 = f(f(dx * dx) + f(dy * dy));
      }
      this.desiredVelocity = { x: dx, y: dy };
      const v = this.velocity;
      const blend = want2 < f(f(v.y * v.y) + f(v.x * v.x)) ? decel : accel;
      const keep = f(1 - blend);
      const vy = f(f(dy * blend) + f(v.y * keep)), vx = f(f(blend * dx) + f(keep * v.x));
      if (f(f(vy * vy) + f(vx * vx)) <= REST) this.velocity = { x: 0, y: 0 };
      else {
        this.velocity = { x: vx, y: vy };
        if (this.animActive === kOn) {
          this.lateralX.active = false; this.lateralY.active = false;
          if (this.introActive) this.cancelIntro();
        }
        const p = { x: f(vx + cam.x), y: f(vy + cam.y), z: cam.z };
        this.clamp(p);
        this.setCurrent(p);
      }
    }
    if (this.inputAllowed === kRequestingOn) this.inputAllowed = kOn;
  }

  /** `m_inputParameters[type]` (`UpdateInputParameters` 0xb60190): GameOptions
   * `Camera.Input.{Mouse,StickPC,Keyboard}` with the mouse and pad move speeds scaled by
   * `MouseCameraSensitivity04` / `PadCameraSensitivity04`. */
  private inputParams(type: number): { moveSpeed: number; accel: number; decel: number } {
    if (type === kMouse) return { moveSpeed: f(this.o(42, 0) * this.o(51, 1)), accel: this.o(44, 0), decel: this.o(45, 0) };
    if (type === kStick) return { moveSpeed: f(this.o(46, 0) * this.o(52, 1)), accel: this.o(47, 0), decel: this.o(48, 0) };
    return { moveSpeed: this.o(43, 0), accel: this.o(49, 0), decel: this.o(50, 0) };
  }

  /** `ParseZoomInput` 0xb63f40, from the disassembly: nothing while `m_IgnoreInputTime > 0`. The
   * wheel (`CameraZoomIn`/`Out`, value v: 1.2 a notch) divides or multiplies the desired distance
   * by (factor - 1)·v + 1 (`InputFactorPC` 1.16, `InputFactorPCPad` while a pad trigger is
   * pressed), unless it is already at `m_zoom.min` / `max`; during the intro the zoom stops the
   * intro at the current distance. `CameraZoomToggle` pressed goes to `m_zoom.min` above
   * `ToggleThresholdDistance` and to `max` otherwise (`ResetButtonInputChannels`). Returns
   * whether it zoomed. */
  private parseZoomInput(): boolean {
    if (this.ignoreInputTime > 0) return false;
    const ci = this.in, val = ci.values, f1 = ci.filter[1], f2 = ci.filter[2];
    const pad = ((f2 & 2) !== 0 && val[17] > 0) || ((f2 & 4) !== 0 && val[18] > 0);
    const factor = pad ? this.o(5, 0) : this.o(4, 0);
    let z: number;
    if ((f1 & 0x80) !== 0 && val[15] > 0) {
      if (this.introActive) { this.introActive = false; this.setAnimActive(false); z = this.pos.z; }
      else {
        if (!(this.desired.z > this.zoomMin)) return false;
        z = f(this.desired.z / f(f(f(factor - 1) * val[15]) + 1));
      }
    } else if ((f2 & 1) !== 0 && val[16] > 0) {
      if (this.introActive) { this.introActive = false; this.setAnimActive(false); z = this.pos.z; }
      else {
        if (!(this.zoomMax > this.desired.z)) return false;
        z = f(f(f(f(factor - 1) * val[16]) + 1) * this.desired.z);
      }
    } else if ((f1 & 0x40) !== 0 && (ci.states[14] & 0xa0) !== 0) {
      z = this.pos.z > this.o(6, 0) ? this.zoomMin : this.zoomMax;
      ci.resetButtons();
    } else return false;
    this.setZoom(z);
    return Math.abs(z) > EPS;
  }

  /** `SetZoom` 0xb63e70: the distance clamped to `m_zoom` (min below it, NaN included), the
   * position clamped to the world, the desired position, and a zoom-only animation of
   * `Zoom.AnimTime`. */
  private setZoom(zoom: number): void {
    this.cameraMoved = true;
    let z = this.zoomMin;
    if (this.zoomMin <= zoom) { z = this.zoomMax; if (zoom <= this.zoomMax) z = zoom; }
    const d = { x: this.pos.x, y: this.pos.y, z };
    this.clamp(d);
    this.desired = { ...d };
    this.initAnimation({ ...this.pos }, d, this.o(7, 0), 0);
    this.lateralX.active = false; this.lateralY.active = false;
  }

  /** `UpdateAnimation` 0xb64230. */
  private updateAnimation(): void {
    if (this.animActive !== kOn || !(this.allowUpdate || this.introActive) || this.introPaused) return;
    if (this.holdLeft > 0) { this.holdLeft = f(this.holdLeft - DT); return; }
    const np = { ...this.pos };
    let running = 0;
    const anims: [Cubic, 'x' | 'y' | 'z'][] = [[this.lateralX, 'x'], [this.lateralY, 'y'], [this.zoomAnim, 'z']];
    for (const [a, k] of anims) {
      if (!a.active) continue;
      a.update(DT);
      np[k] = a.position;
      if (a.active) running++;
    }
    if (running === 0) {
      this.setCurrent(np);
      this.desired = { ...np };
      this.setAnimActive(false);
      this.introActive = false;
      this.centreTarget = { x: np.x, y: np.y };
    } else {
      this.clamp(np);
      this.setCurrent(np);
    }
  }

  /** `UpdateEntityTracking` 0xb622f0. */
  private updateTracking(): void {
    if (this.trackingMode === kNoTracking) return;
    const objs = this.input?.objects ?? [];
    const crateDrop = !!this.input && (this.input.flags & 1) !== 0;
    const cx = this.pos.x, cy = this.pos.y;
    let prevCentreX = this.centreTarget.x, prevCentreY = this.centreTarget.y;
    const prevInterest = this.trackedInterest;
    let nudgeX = 0, nudgeY = 0;
    let newNudgeX = 0, newNudgeY = 0;
    this.timeTracking = f(this.timeTracking + DT);
    let newX = prevCentreX, newY = prevCentreY;
    if (this.trackingMode === kAverage) { newX = 0; newY = 0; }
    let newId = 0xffffffff, newInterest = kInterest_None, newManual = -1;
    let checking = true, foundPrevious = false;
    for (let level = kInterest_Max; level !== kInterest_None; level--) {
      const list = objs.filter(o => o.interest >= level && o.interest <= kInterest_Max && !(this.ignoreCrates && crateDrop && o.crate));
      if (list.length) {
        let mostRecent = -1;
        for (const o of list) {
          const inside = this.borderContains(o.x, o.y);
          const [dx, dy] = this.borderDistance(o.x, o.y);
          const id = ((o.idx * 0x100) + o.sub) >>> 0;
          if (id === this.trackedId) { this.manualOverride = o.manual; foundPrevious = true; this.trackedInterest = level; }
          if (this.trackingMode === kMostRecent) {
            if (checking && mostRecent < o.birth) {
              newX = o.x; newY = o.y; mostRecent = o.birth;
              if (!inside) { newNudgeX = dx; newNudgeY = dy; }
              newInterest = level; newId = id; newManual = o.manual;
            }
          } else if (checking) {
            newX = f(newX + o.x); newY = f(newY + o.y);
            if (!inside) { newNudgeX = f(newNudgeX + dx); newNudgeY = f(newNudgeY + dy); }
            newInterest = level; newId = id; newManual = o.manual;
          }
          if (id === this.trackedId) { prevCentreX = o.x; prevCentreY = o.y; nudgeX = dx; nudgeY = dy; }
        }
        checking = false;
        if (this.trackingMode === kAverage) { newX = f(newX * (1 / list.length)); newY = f((1 / list.length) * newY); }
      }
      if (!checking && foundPrevious) break;
    }
    const prevId = this.trackedId;
    let take = false;
    if ((prevId === newId && prevInterest !== newInterest) || prevId !== newId) {
      let equal = false;
      if (newInterest === this.trackedInterest) equal = this.o(34, 2.8) <= this.timeTracking && this.timeTracking !== this.o(34, 2.8);
      if (this.trackedInterest < newInterest) take = !(this.timeTracking <= this.o(35, 0.8)) || equal;
      else take = !(foundPrevious || this.timeTracking <= this.o(36, 0.6)) || equal;
    }
    if (take) {
      this.borderNudge = { x: newNudgeX, y: newNudgeY };
      if (prevId !== newId) this.timeTracking = 0;
      this.manualOverride = newManual;
      this.trackedId = newId;
      this.trackedInterest = newInterest;
      nudgeX = newNudgeX; nudgeY = newNudgeY;
      prevCentreX = newX; prevCentreY = newY;
    } else {
      this.borderNudge = { x: nudgeX, y: nudgeY };
    }
    this.borderTarget = { x: f(nudgeX + cx), y: f(nudgeY + cy) };
    const dcx = f(this.centreTarget.x - prevCentreX), dcy = f(this.centreTarget.y - prevCentreY);
    const delta = f(Math.sqrt(f(f(dcx * dcx) + f(dcy * dcy))));
    const bl = f(this.o(16, 0.16));
    this.centreTarget = { x: prevCentreX, y: prevCentreY };
    const maxStep = f(this.maxSpeed * DT);
    // Float32 operation for operation as 0xb6287b-0xb62929 / 0xb62d86 (the order of the products
    // and sums decides 1-ulp differences of the immediate target).
    const bl1 = f(1 - bl);
    let bx = f(f(bl * this.borderTarget.x) + f(bl1 * cx)), by = f(f(this.borderTarget.y * bl) + f(cy * bl1));
    {
      let vx = f(bx - cx), vy = f(by - cy);
      const l2 = f(f(vx * vx) + f(vy * vy)), l = f(Math.sqrt(l2));
      if (maxStep < l) {
        if (EPS < l2) { const inv = f(1 / l); vx = f(vx * inv); vy = f(vy * inv); }
        bx = f(cx + f(vx * maxStep)); by = f(f(vy * maxStep) + cy);
      }
    }
    const cl = f(this.o(17, 0.1)), cl1 = f(1 - cl);
    let nx = f(f(cl1 * this.centrePos.x) + f(cl * this.centreTarget.x)), ny = f(f(cl1 * this.centrePos.y) + f(this.centreTarget.y * cl));
    {
      let vx = f(nx - this.centrePos.x), vy = f(ny - this.centrePos.y);
      const l2 = f(f(vx * vx) + f(vy * vy)), l = f(Math.sqrt(l2));
      if (maxStep < l) {
        if (EPS < l2) { const inv = f(1 / l); vx = f(vx * inv); vy = f(vy * inv); }
        ny = f(f(vy * maxStep) + this.centrePos.y); nx = f(f(vx * maxStep) + this.centrePos.x);
      }
    }
    this.centrePos = { x: nx, y: ny };
    let tx = cx, ty = cy;
    let manualBranch = false;
    const toManual = (): void => {
      this.cameraMode = kManual;
      this.ignoreCrates = true;
      tx = cx; ty = cy;
      manualBranch = true;
    };
    if (this.cameraMode === kKeepWithinBorder) {
      // The stick or a camera key (`m_PadActive`) makes the camera manual even while the worm
      // targets; the mouse only when it does not.
      if (!this.isOffscreen(this.centreTarget.x, this.centreTarget.y)) { tx = bx; ty = by; if ((this.mouseActive && !this.usingTargeted) || this.padActive) toManual(); }
      else if (!this.usingTargeted) { this.centre(); tx = this.centrePos.x; ty = this.centrePos.y; if (this.mouseActive || this.padActive) toManual(); }
      else { tx = bx; ty = by; if (this.padActive) toManual(); }
    } else if (this.cameraMode === kLockToActivity) {
      const p = { x: this.centreTarget.x, y: this.centreTarget.y, z: this.pos.z };
      this.clamp(p);
      const r = this.o(9, 10);
      tx = this.centrePos.x; ty = this.centrePos.y;
      if ((p.y - cy) ** 2 + (p.x - cx) ** 2 < r * r) { this.cameraMode = kKeepWithinBorder; this.borderTarget = { x: tx, y: ty }; }
      if ((this.mouseActive && !this.usingTargeted) || this.padActive) toManual();
    } else if (this.cameraMode === kManual) {
      const lvl = this.trackedInterest;
      const timeout = this.manualOverride !== -1 ? this.manualOverride : lvl >= 3 && lvl <= 12 ? this.o(24 + lvl - 3, 3.4028235e38) : 3.4028235e38;
      if (((1 < delta) <= (this.secondsWithoutManual < timeout) && this.removeManual) || timeout <= this.secondsWithoutManual) {
        if (this.isOffscreen(this.centreTarget.x, this.centreTarget.y)) { this.centre(); tx = this.centrePos.x; ty = this.centrePos.y; }
        else { this.cameraMode = kKeepWithinBorder; tx = bx; ty = by; }
      }
      if ((this.mouseActive && !this.usingTargeted) || this.padActive) toManual();
    }
    if (!manualBranch && f(1e-4) < f(f(f(cy - ty) * f(cy - ty)) + f(f(cx - tx) * f(cx - tx)))) this.lookAtImmediate(tx, ty);
    else if (manualBranch) { /* the camera stays where the user put it */ }
    if (this.removeManual && this.cameraMode !== kManual) this.removeManual = false;
  }

  /** `LogicalUpdateCameraShake` 0xb619f0. */
  private updateShake(): void {
    const s = this.shake;
    if (s.state === kInactive) {
      this.noiseOff = { x: 0, y: 0, z: 0 };
    } else {
      let mx = s.magX, my = s.magY;
      s.timeActive = f(s.timeActive + DT);
      if (s.state === kShaking) {
        if (!(s.timeActive < s.duration)) { s.state = kFading; s.timeFading = 0; }
      } else if (s.state === kFading) {
        const tf = f(DT + s.timeFading);
        s.timeFading = tf;
        if (tf < s.fade) { const k = tf / s.fade; mx = f(mx + (0 - mx) * k); my = f(my + k * (0 - my)); }
        else { s.state = kInactive; mx = 0; my = 0; }
      }
      const t = s.timeActive;
      const w = f(t * 0.47746482491493225);
      const cx = f(Math.cos(w * s.freqX) * mx), cy = f(Math.cos(w * s.freqY) * my);
      const ox = f(cx + mx * valueNoise(f(s.freqX * Math.abs(t))));
      const oy = f(cy + valueNoise(f(Math.abs(t) * s.freqY)) * my);
      const p = { x: f(ox + this.pos.x), y: f(oy + this.pos.y), z: f(this.pos.z + 0) };
      this.clamp(p);
      this.noiseOff = { x: f(p.x - this.pos.x), y: f(p.y - this.pos.y), z: f(p.z - this.pos.z) };
    }
    if (this.pulse > 0) {
      this.pulse = f((0 - this.pulse) * 4 * DT + this.pulse);
      this.noiseOff.z = -this.pulse;
    }
  }

  /** `GraphicUpdate` 0xb616e0: the push when the camera moved; `m_AllowUpdate`. */
  private graphicUpdate(): void {
    if (this.graphicMoved) { this.graphicMoved = false; this.push(); }
    this.allowUpdate = true;
  }

  /** `UpdateGraphicCameras` 0xb61280. */
  private push(): void {
    const n = this.noiseOff;
    let x = f(this.pos.x + n.x), y = f(this.pos.y + n.y), z = f(this.pos.z + n.z);
    const lookX = f(n.x + this.lookAt.x), lookY = f(n.y + this.lookAt.y), lookZ = f(n.z + this.lookAt.z);
    const v = this.desiredVelocity;
    if (f(f(v.x * v.x) + f(v.y * v.y)) < EPS) { x = Math.trunc(x); y = Math.trunc(y); z = Math.trunc(z); }
    // normalisedScale in float32: (z - min) / (max - min); a zoom resting at the integer below
    // the option's minimum gives a slightly negative border scale (animals-A: -6.75e-6).
    const zoomParam = this.zoomMax > this.zoomMin ? f(f(z - this.zoomMin) / f(this.zoomMax - this.zoomMin)) : 0;
    const bgZ = f(this.zoomMin + f(f(this.maxUpperLimit - this.zoomMin) * zoomParam));
    this.borderScale = zoomParam;
    this.pushed = { x, y, z, lookX, lookY, lookZ, bgZ, zoomParam };
  }

  /** `ClampToWorldBounds` 0xb60710 (the view-size corners of a perspective frustum looking down
   * z; then the cavern rule; then every component rounded half away from zero). */
  private clamp(p: { x: number; y: number; z: number }): void {
    if (this.bl.x === 0 && this.tr.x === 0 && this.bl.y === 0 && this.tr.y === 0) return;
    const hx = p.z * FRUSTUM_X, hy = p.z * this.fy;
    const minX = Math.max(this.bl.x, this.bl.x + hx), maxX = Math.min(this.tr.x, this.tr.x - hx);
    const minY = Math.max(this.bl.y, this.bl.y + hy), maxY = Math.min(this.tr.y, this.tr.y - hy);
    if (maxX <= minX) p.x = f((maxX + minX) * 0.5);
    else if (p.x < minX) p.x = minX;
    else if (maxX < p.x) p.x = maxX;
    if (minY < maxY) { if (p.y < minY) p.y = minY; else if (maxY < p.y) p.y = maxY; }
    else p.y = f((maxY + minY) * 0.5);
    const inp = this.input;
    if (inp && (inp.flags & 4) !== 0) {
      const [l, t, r, b] = inp.land;
      const halfW = this.pushed.z * FRUSTUM_X, halfH = this.pushed.z * this.fy;
      const minCX = halfW + l;
      const top = t - halfH + ((t + b) * 0.5 <= inp.water ? this.o(39, 1000) : 0);
      const maxCX = r - halfW;
      p.x = minCX < maxCX ? Math.max(minCX, Math.min(p.x, maxCX)) : (minCX + maxCX) * 0.5;
      p.y = top <= minY ? (minY + top) * 0.5 : Math.max(minY, Math.min(p.y, top));
    }
    p.x = p.x <= 0 ? Math.trunc(p.x - 0.5) : Math.trunc(p.x + 0.5);
    p.y = p.y <= 0 ? Math.trunc(p.y - 0.5) : Math.trunc(p.y + 0.5);
    p.z = p.z <= 0 ? Math.trunc(p.z - 0.5) : Math.trunc(p.z + 0.5);
  }
}

/** The tick input at `p`, `end` its end (the view's landscape records follow). */
function decodeTickInput(dv: DataView, p: number, end: number): TickInput {
  const fl = (): number => { const v = dv.getFloat32(p, true); p += 4; return v; };
  const time = fl();
  const flags = dv.getUint8(p); p += 1;
  const introFinish = fl(), introDuration = fl();
  const bl: [number, number] = [fl(), fl()];
  const tr: [number, number] = [fl(), fl()];
  const water = fl();
  const land: [number, number, number, number] = [fl(), fl(), fl(), fl()];
  const landScaleX = fl();
  const n = dv.getUint16(p, true); p += 2;
  const objects: Tracked[] = [];
  for (let i = 0; i < n; i++) {
    const idx = dv.getUint32(p, true), sub = dv.getUint32(p + 4, true), interest = dv.getInt32(p + 8, true);
    p += 12;
    const manual = fl(), birth = fl(), x = fl(), y = fl();
    const of = dv.getUint8(p); p += 1;
    objects.push({ idx, sub, interest, manual, birth, x, y, crate: (of & 1) !== 0, noComment: (of & 2) !== 0 });
  }
  // crates/sim/src/view/camera.rs `parse_input_tail`.
  const centre: CentreInput = { team: 0xffffffff, flags: 0, wormTeam: 0, x: 0, y: 0, key: 0, cx: 0, cy: 0 };
  if (p + 29 <= end) {
    centre.team = dv.getUint32(p, true); p += 4;
    centre.flags = dv.getUint8(p); p += 1;
    centre.wormTeam = dv.getUint32(p, true); p += 4;
    centre.x = fl(); centre.y = fl();
    centre.key = dv.getUint32(p, true); p += 4;
    centre.cx = fl(); centre.cy = fl();
  }
  let endZoom = kZoomClose, endCustom = 0;
  if (p + 5 <= end) {
    endZoom = dv.getUint8(p); p += 1;
    endCustom = fl();
  }
  return { time, flags, introFinish, introDuration, bl, tr, water, land, landScaleX, objects, centre, endZoom, endCustom };
}
